// Renders the React Router tree from the frontend route manifest.

import React, { useMemo } from 'react';
import { Navigate, Route, Routes } from 'react-router';
import CollectionRoute from '@/content/routes/CollectionRoute';
import DocumentRoute from '@/content/routes/DocumentRoute';
import HeaderLayout from '../layouts/HeaderLayout';
import LibraryRoute from '../pages/library/LibraryRoute';
import { useAuth } from '../hooks/useAuth';
import { routeManifest, type FrontendRouteEntry } from '@/app/routing/manifest';

type RequireAuthProps = {
  children: React.ReactElement;
};

const RequireAuth: React.FC<RequireAuthProps> = ({ children }) => {
  const { user, isAuthInitialized } = useAuth();

  if (!isAuthInitialized) {
    return null;
  }

  return user ? children : <Navigate to="/library?dialog=login" replace />;
};

const routeElementFor = (
  entry: FrontendRouteEntry
): React.ReactElement | null => {
  const element =
    entry.content?.type === 'document' ? (
      <DocumentRoute fallbackPath={entry.content.fallbackPath} />
    ) : entry.content?.type === 'collection' ? (
      <CollectionRoute
        collectionId={entry.content.collectionId}
        fallbackPath={entry.content.fallbackPath}
      />
    ) : (
      entry.element
    );

  if (!element) {
    return null;
  }

  if (entry.auth === 'authenticated') {
    return <RequireAuth>{element}</RequireAuth>;
  }

  return element;
};

const renderRoute = (entry: FrontendRouteEntry) => (
  <Route key={entry.path} path={entry.path} element={routeElementFor(entry)} />
);

export const AppRoutes: React.FC = () => {
  const isDev = import.meta.env.DEV;

  const { redirects, headerRoutes, libraryRoutes, bareRoutes } = useMemo(() => {
    const entries = routeManifest.filter((entry) => !entry.dev || isDev);
    const pageRoutes = entries.filter((entry) => entry.redirect === undefined);

    return {
      redirects: entries.filter((entry) => entry.redirect !== undefined),
      headerRoutes: pageRoutes.filter((entry) => entry.layout === 'header'),
      libraryRoutes: pageRoutes.filter((entry) => entry.layout === 'library'),
      bareRoutes: pageRoutes.filter((entry) => entry.layout === 'bare'),
    };
  }, [isDev]);

  return (
    <Routes>
      {redirects.map((entry) => (
        <Route
          key={entry.path}
          path={entry.path}
          element={<Navigate to={entry.redirect!} replace />}
        />
      ))}

      <Route element={<HeaderLayout />}>{headerRoutes.map(renderRoute)}</Route>

      <Route element={<LibraryRoute />}>{libraryRoutes.map(renderRoute)}</Route>

      {bareRoutes.map(renderRoute)}
    </Routes>
  );
};
